import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { IntervalType, GameType } from 'src/app/gamification/command-center/models/games.model';
import { Budget } from 'src/app/gamification/cv-admin/models/budget.model';
import { BudgetService } from 'src/app/gamification/cv-admin/services/budget/budget.service';
import { GenericMetricFormatterPipe } from 'src/app/reusables/pipes/generic-metric-formatter.pipe';
import { IHGameStandardize, IHSelectedMetric, ParticipantObj } from '../../../../command-center/models/games-parameter.model';
import { GamesParameterHistoryService } from '../../../../command-center/services/games-parameter-history.service';
import { GamesService } from '../../../../command-center/services/games.service';
import { Game, Interval, MetricAverage } from '../../../../command-center/components/games/games-history-health-report/selected-game.model';
import { DatePipe } from '@angular/common';

const MAX_RECENT_DAYS = 30; // Max number of days in which a game is considered recent

@Component({
    selector: 'cv-games-details',
    templateUrl: './games-details.component.html',
    styleUrl: './games-details.component.scss'
})

export class GamesDetailsComponent implements OnInit, OnChanges {
    @ViewChild('referenceElement1') referenceElement1!: ElementRef;
    @ViewChild('referenceElement2') referenceElement2!: ElementRef;
    @Input() intervalSelected: Interval[];
    @Input() selectedGame: Game | null = null;
    @Input() endDate: string = null;
    @Input() participants: ParticipantObj[];
    @Input() inAddEdit: boolean;
    @Output() editClicked = new EventEmitter<void>();
    averageValuesCache = new Map<string, { previous: any[]; current: any[] }>();

    GameTypeEnum = GameType;
    gameHistory: IHGameStandardize = null
    gs = null;
    isLoading: boolean = true;
    sumCoinsHistory: number = 0;
    sumBadgesHistory: number = 0;
    firstAverageMetricValue: number = null;
    budgetHistory: Budget = {
        budgetList: [],
        totalAmount: null,
        initBudgetList: []
    };
    metricList: IHSelectedMetric[] = [];
    gameMetric: IHSelectedMetric;
    qualifier: IHSelectedMetric;
    previousMetricResults = null;
    currentMetricResults = null;
    allMetrics = [];

    constructor(
        private readonly metricFormatter: GenericMetricFormatterPipe,
        private readonly cdRef: ChangeDetectorRef,
        private readonly gamesParameterHistoryService: GamesParameterHistoryService,
        private readonly gamesService: GamesService,
        private readonly budgetService: BudgetService,
        private readonly datePipe: DatePipe,
    ) {
        this.gs = this.gamesService.data;
    }

    ngOnInit(): void {
        this.loadDataBudget();
    }

    ngAfterViewChecked(): void {
        if (this.referenceElement1 && this.referenceElement2) {
            const refWidthLeft = this.referenceElement1.nativeElement.offsetWidth;
            const refWidthRight = this.referenceElement2.nativeElement.offsetWidth;
            const targetsLeft = document.querySelectorAll<HTMLElement>('.game-title-left');
            const targetsRight = document.querySelectorAll<HTMLElement>('.game-title-right');

            targetsLeft.forEach(el => {
                el.style.minWidth = `${refWidthLeft}px`;
            });

            targetsRight.forEach(el => {
                el.style.minWidth = `${refWidthRight}px`;
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.endDate?.currentValue ?? changes.selectedGame?.currentValue?.RuleID) {
            this.selectedGame = changes.selectedGame?.currentValue ?? this.selectedGame;
            this.endDate = changes.endDate?.currentValue?.split('T')[0] ?? this.endDate;

            if (this.selectedGame.RuleID > 0 && this.endDate) {
                this.gamesParameterHistoryService.loadGameHistoryParams(this.selectedGame.RuleID, this.endDate)
                    .then((game) => {
                        this.gameHistory = game;
                    })
                    .finally(() => {
                        this.firstAverageMetricValue = null;
                        this.getMetricsHistory();
                        this.continueChangesFlow();
                    });
            }
        }

        if (changes.participants?.currentValue) {
            this.participants = changes.participants?.currentValue;
            this.sumCoinsHistory = this.participants.filter((a: any) => a.calculatedCoins >= 0)
                .reduce((sum, current) => sum + current.calculatedCoins, 0);
            this.sumBadgesHistory = this.participants.reduce((sum, current) => sum + current.badges.length, 0);
        }
    }

    private async continueChangesFlow(): Promise<void> {
        this.isLoading = true;

        while (!this.participants?.length || !this.gameHistory) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        await this.CalculateAverageInterval();

        const previousAverageMap = new Map(
            (this.previousMetricResults ?? []).map(metric => [
                metric.MetricName,
                {
                    averageValue: metric.averageValue,
                    formattedAverageValue: metric.formattedAverageValue
                }
            ])
        );

        const currentAverageMap = new Map(
            (this.currentMetricResults ?? []).map(metric => [
                metric.MetricName,
                {
                    averageValue: metric.averageValue,
                    formattedAverageValue: metric.formattedAverageValue
                }
            ])
        );

        if (Array.isArray(this.metricList)) {
            this.metricList = this.metricList.map(item => {
                const previousMetric = (previousAverageMap.get(item.MetricDetail.Abbrev) ?? {}) as Partial<MetricAverage>;
                const currentMetric = (currentAverageMap.get(item.MetricDetail.Abbrev) ?? {}) as Partial<MetricAverage>;

                return {
                    ...item,
                    FormattedPreviousAverage: this.getFormattedData(previousMetric.averageValue, item.MetricDetail.ConversionType, item.MetricDetail.NumericTypeValue),
                    FormattedCurrentAverage: this.getFormattedData(currentMetric.averageValue, item.MetricDetail.ConversionType, item.MetricDetail.NumericTypeValue),
                    PreviousAverage: previousMetric.averageValue ?? 0,
                    CurrentAverage: currentMetric.averageValue ?? 0
                };
            });

            this.allMetrics = [...this.metricList];
        }

        if (this.gameMetric && typeof this.gameMetric === "object") {
            const previousMetric = (previousAverageMap?.get(this.gameMetric.MetricDetail.Abbrev) ?? {}) as Partial<MetricAverage>;
            const currentMetric = (currentAverageMap?.get(this.gameMetric.MetricDetail.Abbrev) ?? {}) as Partial<MetricAverage>;

            Object.assign(this.gameMetric, {
                FormattedPreviousAverage: this.getFormattedData(previousMetric.averageValue, this.gameMetric.MetricDetail.ConversionType, this.gameMetric.MetricDetail.NumericTypeValue),
                FormattedCurrentAverage: this.getFormattedData(currentMetric.averageValue, this.gameMetric.MetricDetail.ConversionType, this.gameMetric.MetricDetail.NumericTypeValue),
                PreviousAverage: previousMetric.averageValue ?? 0,
                CurrentAverage: currentMetric.averageValue ?? 0
            });

            this.allMetrics.push(this.gameMetric);
        }

        if (this.qualifier && typeof this.qualifier === "object") {
            const previousMetric = (previousAverageMap?.get(this.qualifier.MetricDetail.Abbrev) ?? {}) as Partial<MetricAverage>;
            const currentMetric = (currentAverageMap?.get(this.qualifier.MetricDetail.Abbrev) ?? {}) as Partial<MetricAverage>;

            Object.assign(this.qualifier, {
                FormattedPreviousAverage: this.getFormattedData(previousMetric.averageValue, this.qualifier.MetricDetail.ConversionType, this.qualifier.MetricDetail.NumericTypeValue),
                FormattedCurrentAverage: this.getFormattedData(currentMetric.averageValue, this.qualifier.MetricDetail.ConversionType, this.qualifier.MetricDetail.NumericTypeValue),
                PreviousAverage: previousMetric.averageValue ?? 0,
                CurrentAverage: currentMetric.averageValue ?? 0
            });

            this.allMetrics.push(this.qualifier);
        }

        this.isLoading = false;
        this.cdRef.detectChanges();
    }
    get timeSinceLastUpdate() {
        if (!this.selectedGame || !this.endDate) return '';
        return this.gamesParameterHistoryService.getTimeSinceLastUpdate(this.selectedGame.RuleID, this.endDate);
    }

    get isRecentlyAdded() {
        if (!this.selectedGame || !this.endDate) return '';
        return this.gamesParameterHistoryService.isRecentlyAdded(this.selectedGame.RuleID, this.endDate, MAX_RECENT_DAYS);
    }

    get noRewards(): boolean {
        return this.sumCoinsHistory == 0 && this.sumBadgesHistory == 0;
    }

    editGame() {
        this.editClicked.emit();
        this.gs.modified.Edit(this.selectedGame, { FolderData:{FolderID: this.gs.selectedGame[0].FolderID } });
        $(document).trigger(new jQuery.Event('Event.Games.Setup.Edit'));
    }

    getMetricsHistory() {
        const metrics = this.gamesParameterHistoryService.getMetrics(this.selectedGame.RuleID, this.endDate);
        if (this.selectedGame?.GameType != GameType.Connect3 && this.selectedGame?.GameType != GameType.Blackout) {
            this.gameMetric = metrics.length > 0 ? metrics.find(x => !x.IsQualifier) : null;
            this.qualifier = metrics.find(x => x.IsQualifier);
            this.metricList = [];
        } else {
            this.metricList = metrics.filter(x => !x.IsQualifier).sort((a, b) => a.Position - b.Position);
            this.gameMetric = this.metricList[0];
            this.qualifier = metrics.find(x => x.IsQualifier);
        }

        this.cdRef.detectChanges();
    }

    metricAvg(metricID: number, conversionType: number) {
        const firstValidParticipant = this.participants?.find(p => p.ruleObject !== null);

        if (this.endDate && (this.firstAverageMetricValue !== null || this.participants?.length === 0 || this.participants?.length > 0)) {
            this.isLoading = false;
        } else {
            if (this.endDate == null) {
                this.isLoading = false;
            } else {
                this.isLoading = true;
            }
        }

        if (firstValidParticipant?.ruleObject?.MyRankingObj?.MetricGroupInfos) {
            const foundMetric = firstValidParticipant.ruleObject.MyRankingObj.MetricGroupInfos
                .find(metric => metric.MetricID === metricID);

            this.firstAverageMetricValue = foundMetric ? foundMetric.AverageMetricValue : null;

            if (foundMetric === null && this.participants?.length > 0) {
                this.isLoading = false;
            } else {
                this.isLoading = true;
            }
        } else {
            if (this.participants?.length > 0) {
                this.isLoading = false;
            } else {
                this.isLoading = true;
            }
        }

        return this.gamesService.formatterMetricData(conversionType.toString(), this.firstAverageMetricValue);
    }

    getConnect3InitialPosition() {
        const metricPosition = this.metricList.find(
            (position: any) =>
                position.Position === 0
        );

        if (metricPosition) {
            return {
                direction: metricPosition.MetricDetail.DefaultMetricDirection === 0 ? '<' : '>',
                formattedData: this.getFormattedData(
                    metricPosition.LeftNumber,
                    metricPosition.MetricDetail.ConversionType,
                    metricPosition.MetricDetail.NumericTypeValue,
                )
            };
        }

        return null;
    }

    getFormattedData(value: any, conversionType: any, numericType?: number): string {
        //NumericType 1 is Number according to the enum in clearview-core\ClearView Data Models\Quality\QualityEnums.cs
        const _numericType = typeof numericType !== 'undefined' ? numericType : 1;
        return this.metricFormatter.transform(value, conversionType.toString(), _numericType);
    }

    async loadDataBudget() {
        const budgetList = await this.budgetService.loadBudgets();
        this.budgetHistory.budgetList = budgetList?.map((budget) => ({
            ...budget,
            AmountString: budget.Amount.toString(),
        })).sort((a, b) => a.GroupLevelName.localeCompare(b.GroupLevelName, undefined, { sensitivity: 'base' }));
        this.budgetHistory.initBudgetList = [...this.budgetHistory.budgetList];
        this.budgetHistory.totalAmount = this.budgetHistory.budgetList.reduce((acc, item) => acc + (item.Amount ?? 0), 0);
    }

    get percentageBudget() {
        if (!this.budgetHistory?.budgetList?.length || this.budgetHistory.totalAmount === 0 || this.sumCoinsHistory === 0) {
            return 0;
        }

        const percentage = parseFloat(((this.sumCoinsHistory / this.budgetHistory.totalAmount) * 100).toFixed(2));

        if (percentage % 1 === 0) {
            return parseInt(percentage.toFixed(0));
        }

        return percentage;
    }

    getLocalizedText(key: string, defaultText: string): string {
        return this.gamesService.getLocalizedMessage(key, defaultText);
    }

    private getAverageValuesCacheKey(ruleId: number, displayInterval: string): string {
        return `AverageValuesCache_${ruleId}_${displayInterval}`;
    }

    clickEdit() {
        this.editClicked.emit();
    }

    async CalculateAverageInterval() {
        this.getMetricsHistory();
        if (this.participants) {
            const cacheKey = this.getAverageValuesCacheKey(
                this.selectedGame.RuleID,
                this.gs.selectedInterval[0]?.DisplayStringDate
            );

            if (this.averageValuesCache.has(cacheKey)) {
                const cached = this.averageValuesCache.get(cacheKey);
                this.previousMetricResults = cached.previous;
                this.currentMetricResults = cached.current;
                return;
            }

            const formattedParticipants = this.participants.map(({ participant }) => ({
                ...participant,
                GroupID: participant.RelatedGroupID
            })) || [];

            const startDate = new Date(this.intervalSelected[0].StartDate);
            let endDate: string;
            const previousPeriodDatesArray = this.getPreviousPeriod().split(' - ');
            const previousStart = new Date(previousPeriodDatesArray[0]);
            const previousDate = startDate.toISOString().replace(/\.\d{3}Z$/, '');

            let previousEnd: string;

            if (this.selectedGame.IntervalType === "Day") {
                endDate = previousDate;
                previousEnd = previousStart.toISOString().replace(/\.\d{3}Z$/, '');
            } else {
                const adjustedEndDate = new Date(this.intervalSelected[0].EndDate);
                adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
                endDate = adjustedEndDate.toISOString().replace(/\.\d{3}Z$/, '');
                const adjustedPreviousEnd = new Date(previousPeriodDatesArray[1]);
                previousEnd = adjustedPreviousEnd.toISOString().replace(/\.\d{3}Z$/, '');
            }

            const intervals = [
                { start: previousStart.toISOString().replace(/\.\d{3}Z$/, ''), end: previousEnd },
                { start: previousDate, end: endDate }
            ];

            const combinedMetrics = this.metricList.length
                ? [...this.metricList, ...(this.qualifier ? [this.qualifier] : [])]
                : [this.gameMetric, ...(this.qualifier ? [this.qualifier] : [])];

            if (combinedMetrics[0] != null) {
                const [previousMetrics, currentMetrics] = await Promise.all(
                    intervals.map(({ start, end }) =>
                        Promise.all(
                            combinedMetrics.filter(m => m.MetricID > 0).map(metric => // filter out free space metrics
                                this.gamesService.LoadMetricRanking(
                                    metric.MetricID.toString(),
                                    formattedParticipants,
                                    start,
                                    end,
                                    true,
                                    this.selectedGame.RuleID
                                )
                            )
                        )
                    )
                );

                this.previousMetricResults = previousMetrics;
                this.currentMetricResults = currentMetrics;

                const getAverages = (metricsArray) => {
                    metricsArray.forEach(item => {
                        const averages = item.IntervalAverages ?? [];
                        const total = averages.reduce((sum, curr) => sum + (curr.AverageValue ?? 0), 0);
                        const length = averages.length;
                        item.averageValue = length > 0 ? total / length : 0;
                    });
                };

                getAverages(this.previousMetricResults);
                getAverages(this.currentMetricResults);

                this.averageValuesCache.set(cacheKey, {
                    previous: this.previousMetricResults,
                    current: this.currentMetricResults
                });
            }
        }
    }

    getPreviousPeriod(): string {
        if (!this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return '---';
        }

        const { startDate, endDate } = this.calculatePreviousPeriodDates();
        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    }

    calculatePreviousPeriodDates(): { startDate: Date; endDate: Date } {
        if (!this.intervalSelected?.length ||
            !this.intervalSelected[0]?.StartDate || !this.intervalSelected[0]?.EndDate) {
            return { startDate: new Date(), endDate: new Date() };
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        let endDate = new Date(this.intervalSelected[0].EndDate);

        switch (String(this.selectedGame.IntervalType)) {
            case IntervalType.Day:
                startDate?.setDate(startDate?.getDate() - 1);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 1);
                break;
            case IntervalType.Week:
                startDate?.setDate(startDate?.getDate() - 7);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 6);
                break;
            case IntervalType.Month:
                startDate?.setDate(startDate?.getDate() - 14);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 13);
                break;
            default:
                break;
        }

        return { startDate, endDate };
    }

    formatDate(date: Date): string {
        return this.datePipe.transform(date, 'MM/dd/yyyy') || '';
    }

    getMetricAbbrev(metric: IHSelectedMetric): string {
        if (!this.gameHistory?.HMetricDetails || !metric?.MetricID) return '';

        const match = Object.values(this.gameHistory.HMetricDetails)
            .find(detail => detail.MetricID === metric.MetricID);

        return match?.Abbrev ?? '';
    }

    getGamesHistoryMessages(key: string, defaultText: string): string {
        return this.gamesService.getGamesHistoryMessages(key, defaultText);
    }
}
